import { useAnalytics } from '@/hooks/use-analytics';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';

interface InterestAnalyticsSectionProps {
  initiativeId: string;
  locale: Locale;
}

function SkeletonCards() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-muted rounded-xl p-5 animate-pulse"
        >
          <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-3" />
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function InterestAnalyticsSection({
  initiativeId,
  locale,
}: InterestAnalyticsSectionProps) {
  const isRTL = locale === 'ar';
  // Pass empty string as type — interest analytics are type-agnostic
  const { interestData, loading, error } = useAnalytics(initiativeId, '');

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-4">
      <h3 className={`text-base font-semibold text-foreground ${isRTL ? "text-right" : ""}`}>
        {t(locale, 'interestAnalyticsTitle')}
      </h3>

      {loading && <SkeletonCards />}

      {!loading && error && (
        <p className={`text-sm text-foreground-secondary ${isRTL ? "text-right" : ""}`}>
          {t(locale, 'analyticsUnavailable')}
        </p>
      )}

      {!loading && !error && !interestData && (
        <p className={`text-sm text-foreground-secondary ${isRTL ? "text-right" : ""}`}>
          {t(locale, 'noDataAvailable')}
        </p>
      )}

      {!loading && !error && interestData && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-muted rounded-xl p-5">
            <p className={`text-xs font-medium text-foreground-secondary mb-1 uppercase tracking-wide ${isRTL ? "text-right" : ""}`}>
              {locale === 'ar' ? 'إجمالي المهتمين' : 'Total Interested'}
            </p>
            <p className={`text-2xl font-bold text-foreground ${isRTL ? "text-right" : ""}`}>
              {interestData.total_interested}
            </p>
          </div>
          <div className="bg-muted rounded-xl p-5">
            <p className={`text-xs font-medium text-foreground-secondary mb-1 uppercase tracking-wide ${isRTL ? "text-right" : ""}`}>
              {locale === 'ar' ? 'الأفراد' : 'Individuals'}
            </p>
            <p className={`text-2xl font-bold text-foreground ${isRTL ? "text-right" : ""}`}>
              {interestData.demographics?.enabler_types?.individuals ?? '—'}
            </p>
          </div>
          <div className="bg-muted rounded-xl p-5">
            <p className={`text-xs font-medium text-foreground-secondary mb-1 uppercase tracking-wide ${isRTL ? "text-right" : ""}`}>
              {locale === 'ar' ? 'المنظمات' : 'Organizations'}
            </p>
            <p className={`text-2xl font-bold text-foreground ${isRTL ? "text-right" : ""}`}>
              {interestData.demographics?.enabler_types?.organizations ?? '—'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
